import { Link } from "react-router-dom";
import { Check, BadgeCheck, ArrowRight, Megaphone, Star, Store, GraduationCap, Crown, Users, HelpCircle } from "lucide-react";
import { Layout, PageHeader } from "../components/Layout";
import { WaitlistCapture } from "../components/WaitlistCapture";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  {
    name: "Featured Partner", price: "Starting $1,000", term: "per placement term", icon: Star,
    bestFor: "Local breeders & small dog businesses",
    desc: "Get featured at the top of one marketplace category so buyers see your business first.",
    features: [
      "Top featured placement in ONE category you choose",
      "Highlighted business card with your logo & photos",
      "\"Featured Partner\" badge on your profile",
      "Contact button — leads go straight to you",
      "Ranked above free listings in your category",
    ],
  },
  {
    name: "Marketplace Sponsor", price: "Starting $2,500", term: "per placement term", popular: true, icon: Store,
    bestFor: "Brands that want maximum marketplace exposure",
    desc: "Own premium visibility across the entire E.O.N Marketplace — not just one category.",
    features: [
      "Priority placement across ALL marketplace categories",
      "Featured spot on the Marketplace homepage",
      "Mention in the E.O.N member newsletter",
      "Sponsor badge + fully branded profile",
      "Everything in Featured Partner",
    ],
  },
  {
    name: "Academy Sponsor", price: "Starting $5,000", term: "per placement term", icon: GraduationCap,
    bestFor: "Product & service brands targeting serious owners",
    desc: "Put your brand in front of a high-intent audience that's actively learning and buying.",
    features: [
      "Your brand sponsors an E.O.N Academy section",
      "Logo & placement inside courses and resources",
      "One co-branded downloadable resource",
      "Sponsor badge shown across the Academy",
      "Everything in Marketplace Sponsor",
    ],
  },
  {
    name: "Premier E.O.N Partner", price: "Starting $10,000+", term: "custom term", icon: Crown,
    bestFor: "Category leaders who want to own their space",
    desc: "A platform-wide, category-exclusive partnership — you become THE brand in your space on E.O.N.",
    features: [
      "Featured everywhere across the whole platform",
      "Category exclusivity — no direct competitor runs alongside you",
      "Event & content sponsorship opportunities",
      "A dedicated partner manager",
      "Everything in Academy Sponsor",
    ],
  },
];

const INCLUDED = [
  ["Verified Partner badge", "A trust mark on your profile so buyers know you're a real, reviewed partner."],
  ["Your branding front & center", "Your logo, photos and links displayed on your placement."],
  ["Leads routed to you", "Contact and inquiries go straight to your inbox — no middleman."],
  ["Simple onboarding", "A quick call to set everything up and get you live fast."],
];

const FAQ = [
  ["What exactly am I paying for?", "Placement and visibility on the E.O.N platform for an agreed term — where your brand appears, how prominently, and for how long. The price shown is the starting point; we send exact pricing after you tell us where you'd like to appear."],
  ["Is this a one-time fee or ongoing?", "Placements run for a set term we agree on together. You can renew or upgrade at any time — there's no long-term lock-in."],
  ["Do you guarantee sales or leads?", "No. We guarantee placement and visibility, not specific results or outcomes. What you get is exposure to the Boerboel community."],
  ["How soon do I go live?", "Usually within a few business days of confirming your details and artwork."],
  ["Can I combine options or get something custom?", "Yes — corporate and fully custom partnerships are available by quote. Just ask in the form below."],
];

const VERIFIED_STEPS = [
  "Submit your business & professional information",
  "Provide documentation relevant to your category",
  "E.O.N reviews your marketplace profile",
  "Approved profiles display the E.O.N Verified badge",
];

export default function Partners() {
  const { user } = useAuth();
  return (
    <Layout>
      <PageHeader label="Partner With E.O.N" title="Grow With the Ecosystem"
        subtitle="Put your brand in front of the Boerboel community — sponsor the marketplace and academy, or get E.O.N Verified to stand out as a trusted provider." />

      {/* Sponsorship tiers */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14">
        <div className="flex items-center gap-3 mb-3"><Megaphone size={22} className="text-eon-gold" /><h2 className="eon-heading text-3xl sm:text-4xl text-eon-text">Sponsorship & Placement</h2></div>
        <p className="text-sm text-eon-muted max-w-2xl mb-8">Choose how visible you want to be. Each option is a paid placement on the E.O.N platform for an agreed term — pick the reach that fits your goals. Higher tiers include everything in the tier before it.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((t, i) => (
            <div key={t.name} data-testid={`partner-tier-${i}`} className={`eon-card p-6 flex flex-col relative animate-fade-up ${t.popular ? "ring-2 ring-eon-gold" : ""}`} style={{ animationDelay: `${i * 50}ms` }}>
              {t.popular && <span className="absolute -top-3 right-5 text-[10px] font-bold uppercase tracking-widest bg-eon-gold text-white rounded-full px-3 py-1">Popular</span>}
              <t.icon size={22} className="text-eon-gold" />
              <div className="font-display text-lg font-bold text-eon-text mt-3">{t.name}</div>
              <div className="font-mono text-eon-gold text-lg font-semibold mt-1">{t.price}</div>
              <div className="text-[11px] text-eon-muted uppercase tracking-wider mt-0.5">{t.term}</div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-eon-text bg-eon-surface border border-eon-border rounded-full px-3 py-1.5 self-start">
                <Users size={12} className="text-eon-gold" /> Best for: {t.bestFor}
              </div>
              <p className="text-sm text-eon-muted mt-3">{t.desc}</p>
              <div className="eon-label mt-5 mb-2">What you get</div>
              <ul className="space-y-2 flex-1">
                {t.features.map((f) => <li key={f} className="flex items-start gap-2 text-xs text-eon-text/85"><Check size={14} className="text-eon-gold shrink-0 mt-0.5" /> {f}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-eon-muted mt-4">Corporate & custom partnerships available — custom quote. No guaranteed results or outcomes.</p>

        {/* Included in every partnership */}
        <div className="mt-12">
          <h3 className="eon-heading text-2xl text-eon-text mb-6">Included in every partnership</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INCLUDED.map(([title, body]) => (
              <div key={title} className="eon-card p-5" data-testid={`partner-included-${title}`}>
                <BadgeCheck size={20} className="text-eon-gold" />
                <div className="font-display font-bold text-eon-text mt-3">{title}</div>
                <p className="text-xs text-eon-muted mt-1.5 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="mt-12 grid lg:grid-cols-2 gap-10">
          <div>
            <h3 className="eon-heading text-2xl text-eon-text mb-6">How it works</h3>
            <ol className="space-y-4">
              {[
                ["Tell us where you want to appear", "Use the form below or request a custom quote."],
                ["We send exact options & pricing", "A quick recommendation for your goals and budget."],
                ["Confirm & pay securely", "Pay by card or invoice once you're happy with the plan."],
                ["You go live", "Your placement is set up — usually within a few business days."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="w-7 h-7 rounded-full bg-eon-gold text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <div><div className="font-semibold text-eon-text text-sm">{t}</div><div className="text-xs text-eon-muted mt-0.5">{d}</div></div>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <h3 className="eon-heading text-2xl text-eon-text mb-6 flex items-center gap-2"><HelpCircle size={22} className="text-eon-gold" /> Common questions</h3>
            <div className="space-y-3">
              {FAQ.map(([q, a], i) => (
                <details key={q} className="eon-card p-5 group" data-testid={`partner-faq-${i}`}>
                  <summary className="font-semibold text-eon-text text-sm cursor-pointer list-none flex items-center justify-between">
                    {q} <span className="text-eon-gold group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </summary>
                  <p className="text-xs text-eon-muted mt-3 leading-relaxed">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 rounded-3xl border border-eon-border bg-eon-surface p-8">
          <h3 className="font-display text-2xl font-bold text-eon-text">Become a Partner</h3>
          <p className="text-sm text-eon-muted mt-2 mb-5 max-w-lg">Tell us where you'd like to appear and we'll send options and exact pricing — no obligation.</p>
          <WaitlistCapture subjectType="sponsorship" subjectId="eon-partner" placeholder="Business email" cta="Request Partnership Info" />
        </div>
      </div>

      {/* E.O.N Verified */}
      <div className="bg-eon-surface border-t border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4"><BadgeCheck size={24} className="text-eon-gold" /><h2 className="eon-heading text-3xl sm:text-4xl text-eon-text">E.O.N Verified</h2></div>
            <p className="text-eon-muted leading-relaxed max-w-lg">An optional verification program for breeders and businesses. Verified profiles carry the E.O.N Verified badge across the marketplace so buyers know you've been reviewed. Verification does not imply E.O.N guarantees any business or breeder.</p>
            <ol className="mt-6 space-y-3">
              {VERIFIED_STEPS.map((s, i) => (
                <li key={s} className="flex items-start gap-3 text-sm text-eon-text"><span className="w-6 h-6 rounded-full bg-eon-gold text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span> {s}</li>
              ))}
            </ol>
            <Link to={user ? "/dashboard" : "/register"} data-testid="verified-apply-btn"
              className="inline-flex items-center gap-2 mt-7 rounded-full bg-eon-gold text-white text-sm font-bold px-6 py-3.5 hover:bg-eon-goldhover transition-colors">
              {user ? "Request Verification in Dashboard" : "Create an Account to Apply"} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="rounded-3xl border border-eon-border bg-white p-8">
            <div className="flex items-center gap-2 text-eon-gold"><BadgeCheck size={20} /><span className="font-display font-bold text-eon-text">Trusted by the community</span></div>
            <p className="text-sm text-eon-muted mt-4">List a business on the E.O.N Marketplace, then request verification from your dashboard. Our team reviews each profile before the badge is granted.</p>
            <Link to="/services" className="inline-flex items-center gap-1 mt-5 text-sm font-bold text-eon-gold">Go to the Marketplace <ArrowRight size={15} /></Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
